import { useLocalStorage } from 'hooks';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useLayoutEffect, useState } from 'react';

type ThemePreference = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

const getSystemTheme = (): ResolvedTheme =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const useDarkMode = () => {
  const isEnabled = useFeatureFlagEnabled('Theme-Select');

  const [themePreference, setThemePreference] =
    useLocalStorage<ThemePreference>({
      key: 'atarim-theme-preference',
      defaultValue: 'light',
      getInitialValueInEffect: false,
    });

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('light');

  // Single effect to handle all theme logic
  useLayoutEffect(() => {
    // Only explicitly disable when the flag is false.
    // Treat undefined (not loaded) as enabled to keep UX working in injectable script.
    if (isEnabled === false) {
      document.documentElement.classList.remove('dark');
      document
        .getElementById('root-atarim')
        ?.shadowRoot?.getElementById('app-wrapper')
        ?.classList.remove('dark');
      setResolvedTheme('light');
      return;
    }

    const resolveCurrentTheme = (): ResolvedTheme => {
      switch (themePreference) {
        case 'dark':
          return 'dark';
        case 'light':
          return 'light';
        case 'system':
          return getSystemTheme();
        default:
          return 'light';
      }
    };

    const applyTheme = (theme: ResolvedTheme) => {
      document.documentElement.classList.toggle('dark', theme === 'dark');
      document
        .getElementById('root-atarim')
        ?.shadowRoot?.getElementById('app-wrapper')
        ?.classList.toggle('dark', theme === 'dark');

      setResolvedTheme(theme);
    };

    // Apply initial theme
    const currentTheme = resolveCurrentTheme();
    applyTheme(currentTheme);

    // Only listen for system changes if using system preference
    if (themePreference === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleSystemChange = (e: MediaQueryListEvent) => {
        const newTheme = e.matches ? 'dark' : 'light';
        applyTheme(newTheme);
      };

      mediaQuery.addEventListener('change', handleSystemChange);
      return () => mediaQuery.removeEventListener('change', handleSystemChange);
    }
  }, [isEnabled, themePreference]);

  return [resolvedTheme, setThemePreference, themePreference] as const;
};

export default useDarkMode;
